import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import Footer from '../components/Footer';

const PrivacyPolicyPage = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="border-b border-slate-200">
        <div className="max-w-4xl mx-auto px-4 py-6">
          <Link to="/" className="inline-flex items-center text-indigo-600 hover:text-indigo-700 mb-4">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Formlio
          </Link>
          <h1 className="text-3xl font-bold text-slate-900">Privacy Policy</h1>
          <p className="text-slate-600 mt-2">Last updated: February 10, 2026</p>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 py-12">
        <div className="prose prose-slate max-w-none">
          
          <section className="mb-10">
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">Introduction</h2>
            <p className="text-slate-700 leading-relaxed">
              Formlio ("we," "our," or "us") is an online form builder and workflow automation platform. 
              This Privacy Policy explains how we collect, use, store, and protect your information when 
              you use our services, including how we handle data from third-party integrations such as 
              Google APIs.
            </p>
            <p className="text-slate-700 leading-relaxed mt-4">
              By using Formlio, you agree to the terms of this Privacy Policy. If you do not agree, 
              please do not use our services.
            </p>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">1. Google User Data Access</h2>
            <p className="text-slate-700 leading-relaxed">
              Formlio integrates with Google services to enhance your workflow automation. We access 
              Google user data <strong>only after you provide explicit consent</strong> through Google's 
              OAuth authentication process.
            </p>
            <h3 className="text-xl font-medium text-slate-800 mt-6 mb-3">Data We Access:</h3>
            <ul className="list-disc pl-6 text-slate-700 space-y-2">
              <li>
                <strong>Google Sheets:</strong> We access only the specific spreadsheets you select 
                for form submission storage. We do not access spreadsheets you have not explicitly connected.
              </li>
              <li>
                <strong>Google Drive Metadata:</strong> We access limited metadata (file names and IDs) 
                solely to display a list of your spreadsheets for selection. We do not access, read, 
                or store the contents of files you have not selected.
              </li>
              <li>
                <strong>Google Calendar:</strong> When you enable the Google Calendar integration, we 
                access your calendar only to create events based on form submissions. Calendar access 
                is limited to event creation in calendars you explicitly authorize.
              </li>
            </ul>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">2. How We Use Google User Data</h2>
            <p className="text-slate-700 leading-relaxed">
              We use Google user data strictly to perform actions you explicitly request:
            </p>
            <ul className="list-disc pl-6 text-slate-700 space-y-2 mt-4">
              <li>
                <strong>Form Submission Storage:</strong> When you connect a Google Sheet to a form, 
                we write form submission data to that spreadsheet.
              </li>
              <li>
                <strong>Calendar Event Creation:</strong> When you enable Google Calendar integration, 
                we create calendar events based on form submission data.
              </li>
              <li>
                <strong>User-Initiated Actions Only:</strong> Data is processed only when an integration 
                is actively enabled by you and triggered by a form submission. We do not access your 
                Google data in the background or without your explicit action.
              </li>
            </ul>
            <p className="text-slate-700 leading-relaxed mt-4">
              <strong>We do not:</strong>
            </p>
            <ul className="list-disc pl-6 text-slate-700 space-y-2 mt-2">
              <li>Use Google user data for advertising or marketing purposes</li>
              <li>Analyze your Google data for purposes unrelated to providing our services</li>
              <li>Access your Google data when integrations are disabled</li>
            </ul>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">3. Data Storage & Security</h2>
            <p className="text-slate-700 leading-relaxed">
              We take the security of your data seriously and implement industry-standard practices:
            </p>
            <ul className="list-disc pl-6 text-slate-700 space-y-2 mt-4">
              <li>
                <strong>OAuth Token Security:</strong> Google OAuth access tokens and refresh tokens 
                are encrypted using AES-256 encryption before storage. Tokens are stored securely 
                and are never exposed in logs or API responses.
              </li>
              <li>
                <strong>Minimal Data Retention:</strong> Formlio does not permanently store the contents 
                of your Google Sheets or Calendar data. We only temporarily process data during the 
                execution of integrations.
              </li>
              <li>
                <strong>Secure Transmission:</strong> All data transmitted between Formlio and Google 
                services uses HTTPS/TLS encryption.
              </li>
              <li>
                <strong>Access Controls:</strong> Access to backend systems is restricted to authorized 
                personnel only, with audit logging enabled.
              </li>
            </ul>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">4. Data Sharing & Selling</h2>
            <div className="bg-indigo-50 border-l-4 border-indigo-500 p-4 my-4">
              <p className="text-slate-800 font-medium">
                Formlio does NOT sell Google user data to any third party.
              </p>
            </div>
            <div className="bg-indigo-50 border-l-4 border-indigo-500 p-4 my-4">
              <p className="text-slate-800 font-medium">
                Formlio does NOT share Google user data with third parties except as necessary to 
                fulfill user-requested integrations (e.g., writing to a Google Sheet you have authorized).
              </p>
            </div>
            <p className="text-slate-700 leading-relaxed mt-4">
              We may share data only in the following limited circumstances:
            </p>
            <ul className="list-disc pl-6 text-slate-700 space-y-2 mt-2">
              <li>With your explicit consent</li>
              <li>To comply with legal obligations or valid legal requests</li>
              <li>To protect the rights, safety, or property of Formlio or our users</li>
            </ul>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">5. User Control & Data Deletion</h2>
            <p className="text-slate-700 leading-relaxed">
              You have full control over your data and integrations:
            </p>
            <ul className="list-disc pl-6 text-slate-700 space-y-2 mt-4">
              <li>
                <strong>Disconnect Integrations:</strong> You can disconnect any Google integration 
                at any time from the Integrations panel in your form settings.
              </li>
              <li>
                <strong>Immediate Revocation:</strong> When you disconnect a Google integration, 
                Formlio immediately deletes the stored OAuth tokens and revokes access to your 
                Google services.
              </li>
              <li>
                <strong>Google Account Settings:</strong> You can also revoke Formlio's access 
                directly from your Google Account settings at{' '}
                <a href="https://myaccount.google.com/permissions" target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:underline">
                  https://myaccount.google.com/permissions
                </a>
              </li>
              <li>
                <strong>Account Deletion:</strong> You may request deletion of your Formlio account 
                and all associated data by contacting us at privacy@formlio.app
              </li>
            </ul>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">6. Google API Services Compliance</h2>
            <div className="bg-slate-100 p-6 rounded-lg">
              <p className="text-slate-700 leading-relaxed">
                Formlio's use and transfer of information received from Google APIs adheres to the{' '}
                <a 
                  href="https://developers.google.com/terms/api-services-user-data-policy" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-indigo-600 hover:underline"
                >
                  Google API Services User Data Policy
                </a>
                , including the Limited Use requirements.
              </p>
              <h3 className="text-lg font-medium text-slate-800 mt-4 mb-2">Limited Use Disclosure:</h3>
              <p className="text-slate-700 leading-relaxed">
                Formlio limits its use of Google user data to providing and improving user-facing 
                features that are visible and prominent in our application's user interface. We do 
                not use Google user data for:
              </p>
              <ul className="list-disc pl-6 text-slate-700 space-y-1 mt-2">
                <li>Serving advertisements</li>
                <li>Training machine learning or AI models unrelated to providing the service</li>
                <li>Any purpose other than providing the specific integration features you enable</li>
              </ul>
            </div>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">7. Other Data We Collect</h2>
            <p className="text-slate-700 leading-relaxed">
              In addition to Google integration data, Formlio collects:
            </p>
            <ul className="list-disc pl-6 text-slate-700 space-y-2 mt-4">
              <li>
                <strong>Account Information:</strong> Email address and password (hashed) for 
                account creation and authentication.
              </li>
              <li>
                <strong>Form Data:</strong> Forms you create and submissions you receive are 
                stored securely on our servers.
              </li>
              <li>
                <strong>Usage Data:</strong> Basic analytics about how you use our service to 
                improve functionality.
              </li>
            </ul>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">8. Cookies</h2>
            <p className="text-slate-700 leading-relaxed">
              Formlio uses essential cookies for authentication and session management. We do not 
              use tracking cookies for advertising purposes.
            </p>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">9. Changes to This Policy</h2>
            <p className="text-slate-700 leading-relaxed">
              We may update this Privacy Policy from time to time. We will notify you of significant 
              changes by posting a notice on our website or sending you an email. Your continued use 
              of Formlio after changes constitutes acceptance of the updated policy.
            </p>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">10. Contact Us</h2>
            <p className="text-slate-700 leading-relaxed">
              If you have questions about this Privacy Policy or our data practices, please contact us:
            </p>
            <div className="mt-4 text-slate-700">
              <p><strong>Email:</strong> privacy@formlio.app</p>
              <p className="mt-2"><strong>Website:</strong> https://formlio.app</p>
            </div>
          </section>

        </div>
      </main>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default PrivacyPolicyPage;
